"use client"

import { useEffect, useRef } from "react"
import { usePathname } from "next/navigation"
import { trackEvent, runWhenPageVisible } from "@/lib/analytics"
import { runWhenAnalyticsReady } from "@/lib/analytics/analytics-ready"

export type GuideKind =
  | "grass-guide"
  | "care-task-pillar"
  | "buying-guide"
  | "grass-types-hub"

/**
 * The single emitter for `guide_viewed`. /guides/[slug] renders four different
 * content components over one URL space; only the grass-guide branch used to
 * emit, so care-task pillars, buying guides, and the grass-types hub were dark.
 * Mounting this from the server page keeps one emitter for all four.
 *
 * Held behind runWhenPageVisible for the same reason useBlogTracking holds
 * blog_article_viewed: posthog-js gates the initial $pageview on visibility, so
 * an ungated mount-time event emits into prerender/background sessions that
 * never receive a $pageview. Held behind runWhenAnalyticsReady because the
 * visibility gate alone still fires inside the hydration commit, before
 * posthog.init() (see that helper).
 */
export function GuideViewTracker({
  slug,
  guideType,
  grassType,
}: {
  slug: string
  guideType: GuideKind
  grassType?: string
}) {
  const pathname = usePathname()
  const firedFor = useRef<string | null>(null)

  useEffect(() => {
    const key = pathname || `/guides/${slug}`
    if (firedFor.current === key) return

    let cancelReady: (() => void) | null = null
    const cancelVisible = runWhenPageVisible(() => {
      if (firedFor.current === key) return
      cancelReady = runWhenAnalyticsReady(() => {
        if (firedFor.current === key) return
        firedFor.current = key
        const props: Record<string, string> = { slug, guide_type: guideType }
        if (grassType) props.grass_type = grassType
        trackEvent("guide_viewed", props)
      })
    })

    return () => {
      cancelVisible()
      cancelReady?.()
    }
  }, [pathname, slug, guideType, grassType])

  return null
}
